import { RootState } from 'store/store';
import { useAppStore } from 'store/store-hooks';
import { convertTransition, selectSbClipPlaybackParams } from 'components/app/_helpers/_app.helper';
import { SbPlay } from 'components/app/storyboard/_mediator/use-sb-cb';
import { useSbPlayerContext } from './sb-player.provider';
import { isImage, isLinkedGroup } from './sb-player.helper';
import { useSbPlayerCb } from './use-sb-player-cb';

export type IdleAction = 'onSeek' | 'onRegionIn' | 'onRegionOut' | 'onIdleEnd';

// NOTE: the damping player cannot be idled

export const useSbIdle = () => {
  const { idleTimeoutID, idleTime, sbSetIdle } = useSbPlayerContext();
  const { getPlaybackReadyPlayers } = useSbPlayerCb();
  const store = useAppStore();

  const handleIdleTime = (clipId: CellClip, cursorTime: EditorTime, cursorCell?: CursorCell) => {
    if (!clipId || isImage(clipId)) return;
    const payload = selectData(store.getState());
    const cell = cursorCell ?? payload.cursorCell;
    const idleTime = getIdleTimeOnSeek(clipId, cursorTime, cell, payload);
    sbSetIdle(idleTime);
  };

  const handleIdle = (playersToPlay: Index[], action: IdleAction, cb: SbPlay) => {
    if (action === 'onIdleEnd') return playersToPlay;

    const payload = selectData(store.getState());
    const { activePlayerIndex, isEmptyCell } = payload.sbPlayer;
    if (isEmptyCell) return playersToPlay;

    const clipId = getClipId(payload);
    if (isImage(clipId)) return playersToPlay;

    const idleVal = getIdleTimeOnPlay(clipId, action, idleTime.current, payload);
    if (!idleVal) {
      sbSetIdle(0);
      return playersToPlay;
    }
    if (action !== 'onRegionOut') {
      if (action !== 'onSeek') sbSetIdle(idleVal);
      idleTimeoutID.current = setTimeout(() => {
        cb(getPlaybackReadyPlayers(), 'onIdleEnd');
        sbSetIdle(0);
      }, idleVal * 1000);
    }
    return playersToPlay.filter((el) => el !== activePlayerIndex);
  };

  const resetIdle = () => {
    clearTimeout(idleTimeoutID.current);
  };

  return { handleIdleTime, handleIdle, resetIdle };
};

/* S E L E C T O R */

type Data = ReturnType<typeof selectData>;

const selectData = (state: RootState) => {
  const cursorCell = state.editor__controls.sbPosition.cell;
  const { markers, storyConfig } = state.editor;
  const { sbClipPlaybackMap } = state.sb;
  return { sbPlayer: state.sb__player, cursorCell, sbClipPlaybackMap, markers, storyConfig };
};

/* H E L P E R S */

const getClipId = (data: Data) => {
  const { activePlayerIndex, playbackClips, linkedGroupIndex } = data.sbPlayer;
  const playbackElement = playbackClips[activePlayerIndex];
  return isLinkedGroup(playbackElement) ? playbackElement![linkedGroupIndex!] : (playbackElement as ClipId);
};

const getIdleTimeOnPlay = (clipId: ClipId, action: IdleAction, idleTime: Seconds, data: Data) => {
  const { storyConfig, sbClipPlaybackMap } = data;
  const { idleStart } = selectSbClipPlaybackParams(clipId, sbClipPlaybackMap!);
  if (idleStart === 0) return 0;
  if (action === 'onRegionIn') return idleStart;
  else if (action === 'onSeek') return idleTime;
  else if (action === 'onRegionOut') {
    const isIdle = idleStart > convertTransition(storyConfig!.transitionTime);
    return isIdle ? idleStart : 0;
  }
};

const getIdleTimeOnSeek = (clipId: ClipId, cursorTime: EditorTime, cursorCell: CursorCell, data: Data) => {
  const { markers, sbClipPlaybackMap } = data;
  const { idleStart } = selectSbClipPlaybackParams(clipId, sbClipPlaybackMap!);
  if (idleStart === 0) return 0;
  const markerTime = markers![cursorCell - 1] ?? 0;
  if (markerTime === cursorTime) return idleStart;
  const cursorCellTime = cursorTime - markerTime;
  const idleTime = idleStart - cursorCellTime;
  return idleTime < 0 ? 0 : idleTime;
};
